import { Stack, styled, useTheme } from 'canoe';
import React, { useCallback, useState } from 'react';

import { TextButton, ValueGroup } from '@candu-ai/craft/ui';
import PlusIcon from 'craft/components/Canvas/SegmentsTab/PlusIcon';
import { BuiltInComponents } from 'craft/constants';
import { InteractionLabelDropdown } from 'craft/containers/Actions/InteractionLabelDropdown';
import { Section } from 'craft/containers/Toolbox/components';
import { useMultiNodes } from 'craft/hooks';
import { Action, CATEGORIES, CollectionTargetLocation, ContentCollection } from 'craft/types';

import { ActionForm } from './ActionForm';
import { InteractionDropdown } from './InteractionDropdown';

const AddNewText = styled.span`
  margin-left: 6px;
`;

interface Props {
  nodeIds: string[];
}

const words = ['First', 'Second', 'Third', 'Fourth', 'Fifth'];

export const ActionsToolbox = ({ nodeIds }: Props) => {
  const theme = useTheme();

  const {
    customActions: maybeCustomActions,
    collection,
    actions: { setCustom },
    type,
  } = useMultiNodes(nodeIds, (node) => ({
    collection: (node?.data?.custom.collections as ContentCollection[] | undefined)?.find(
      (s) => s.targetLocation === CollectionTargetLocation.ActionURL,
    ),
    customActions: node?.data?.custom?.actions,
  }));

  const canAddActions =
    type !== BuiltInComponents.Modal &&
    type !== BuiltInComponents.Hotspot &&
    type !== BuiltInComponents.TourStep &&
    type !== BuiltInComponents.RichTextEditor;

  const customActions = ((maybeCustomActions || []) as Action[]).filter(
    (a: Action) => a.category !== CATEGORIES.DISMISS,
  );

  const dismissAction = (maybeCustomActions || []).find(
    (a: Action) => a.category === CATEGORIES.DISMISS,
  );
  const copyTextAction = (maybeCustomActions || []).find(
    (a: Action) => a.category === CATEGORIES.RICHTEXT,
  );
  const dismissActionArray = dismissAction ? [dismissAction] : [];

  const hasActions = customActions.length > 0;
  const [isAdding, setIsAdding] = useState(!hasActions);

  const setActions = useCallback((actions?: Action[]) => setCustom({ actions }), [setCustom]);

  const addAction = useCallback(
    (newAction: any) => {
      if (newAction) {
        setActions([...(customActions || []), newAction, ...dismissActionArray]);
      }
      setIsAdding(false);
    },
    [customActions, setActions, setIsAdding],
  );

  const updateAction = (index: number) => (newAction: Action) => {
    const newActions = [...customActions, ...dismissActionArray];
    newActions[index] = newAction;

    setActions(newActions);
  };

  const updateDismissLabel = (eventName?: string) => {
    const newDismissAction = { ...dismissAction, eventName };

    setActions([...customActions, newDismissAction]);
  };

  const updateCopyTextLabel = (eventName?: string) => {
    const newCopyTextAction = { ...copyTextAction, eventName };

    setActions([newCopyTextAction]);
  };

  const removeAction = (index: number) => () => {
    const newActions = [...customActions, ...dismissActionArray];
    newActions.splice(index, 1);
    setActions(newActions);

    if (newActions.length === 0) {
      setIsAdding(true);
    }
  };

  return (
    <>
      {canAddActions && (
        <Section id="toolbox-interactions" name="Action on click/tap">
          <Stack>
            {customActions?.map((action, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <ValueGroup label={`${words[index]} Action`} key={index}>
                <ActionForm
                  collection={collection as ContentCollection | undefined}
                  action={action}
                  updateAction={updateAction(index)}
                  removeAction={removeAction(index)}
                  nodeIds={nodeIds}
                />
              </ValueGroup>
            ))}
            {!isAdding && customActions.length < 5 && (
              <TextButton onClick={() => setIsAdding(true)}>
                <PlusIcon color={theme.colors.OnyxGrayL20} width="12px" />
                <AddNewText>Add another action</AddNewText>
              </TextButton>
            )}
            {isAdding && (
              <ValueGroup label="New Action">
                <InteractionDropdown nodeIds={nodeIds} onChange={addAction} />
              </ValueGroup>
            )}
          </Stack>
        </Section>
      )}
      {dismissAction && (
        <Section id="toolbox-interaction-dismiss" name="Dismiss Action">
          <ValueGroup label="Dismiss Interaction label">
            <InteractionLabelDropdown
              value={dismissAction.eventName as string}
              onChange={(eventName: string) => updateDismissLabel(eventName)}
            />
          </ValueGroup>
        </Section>
      )}
      {copyTextAction && (
        <Section id="toolbox-interaction-copy-text" name="Copy Text Action">
          <ValueGroup label="Interaction label">
            <InteractionLabelDropdown
              value={copyTextAction.eventName as string}
              onChange={(eventName: string) => updateCopyTextLabel(eventName)}
            />
          </ValueGroup>
        </Section>
      )}
    </>
  );
};
